import { useEffect, useMemo, useState } from "react";
import { getApiUrl } from "../api";
import "./SmsDonationOptions.css";

const fallbackProviderOptions = {
  etisalat: [
    { amount: 10, shortCode: "6963" },
    { amount: 20, shortCode: "6464" },
    { amount: 50, shortCode: "7785" },
    { amount: 100, shortCode: "7896" },
    { amount: 200, shortCode: "6366" },
    { amount: 500, shortCode: "6303" },
    { amount: 1000, shortCode: "6218" },
  ],
  du: [
    { amount: 10, shortCode: "6963" },
    { amount: 20, shortCode: "6464" },
    { amount: 100, shortCode: "7896" },
    { amount: 200, shortCode: "6366" },
    { amount: 500, shortCode: "6303" },
    { amount: 1000, shortCode: "6218" },
  ],
};

export const fallbackSmsDonationOptions = Object.entries(fallbackProviderOptions).flatMap(([provider, options]) =>
  options.map((option) => ({ ...option, provider, message: "" })),
);

const providerDetails = {
  etisalat: { name: "Etisalat", logo: "/images/img/ethi.jpg" },
  du: { name: "du", logo: "/images/img/du.jpg" },
};

const cleanProvider = (value) => String(value || "").trim().toLowerCase() === "du" ? "du" : "etisalat";

export const normalizeSmsDonationOptions = (payload) => {
  const source = Array.isArray(payload) ? payload : payload?.data || payload?.Data || payload?.items || payload?.options || [];
  if (!Array.isArray(source)) return [];

  return source.map((item) => ({
    id: item.id ?? item.ID,
    provider: cleanProvider(item.provider ?? item.Provider ?? item.operator ?? item.Operator ?? item.telecom),
    amount: Number(item.amount ?? item.Amount ?? item.value ?? item.Value),
    shortCode: String(item.shortCode ?? item.ShortCode ?? item.short_code ?? item.code ?? item.Code ?? item.smsNumber ?? item.SMSNumber ?? item.number ?? "").trim(),
    message: String(item.message ?? item.Message ?? item.messageBody ?? item.MessageBody ?? item.message_body ?? item.messageFormat ?? item.MessageFormat ?? item.keyword ?? item.Keyword ?? item.smsText ?? "").trim(),
    enabled: item.enabled ?? item.isActive ?? item.IsActive ?? true,
  })).filter((item) => item.enabled && item.amount > 0 && /^\d+$/.test(item.shortCode));
};

export const mergeSmsDonationOptions = (configuredOptions = []) => {
  const merged = new Map();
  fallbackSmsDonationOptions.forEach((option) => {
    merged.set(`${option.provider}-${option.amount}`, option);
  });
  configuredOptions.forEach((option) => {
    const key = `${option.provider}-${option.amount}`;
    merged.set(key, { ...merged.get(key), ...option });
  });
  return Array.from(merged.values());
};

export const buildSmsHref = (shortCode, message, userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent) => {
  const recipient = String(shortCode || "").replace(/[^\d+]/g, "");
  const body = String(message || "").trim();
  if (!body) return `sms:${recipient}`;
  const separator = /iPad|iPhone|iPod/i.test(userAgent) ? "&" : "?";
  return `sms:${recipient}${separator}body=${encodeURIComponent(body)}`;
};

const SmsDonationOptions = ({
  className = "",
  language = "en",
  title,
  currency,
  codeLabel,
  iconClass = "fa-solid fa-hands-holding-circle",
  showTitle = true,
  endpoint = process.env.REACT_APP_SMS_DONATION_ENDPOINT || "/api/GetSMSDonationOptions.ashx",
  options,
}) => {
  const [activeProvider, setActiveProvider] = useState("etisalat");
  const [configuredOptions, setConfiguredOptions] = useState(() => normalizeSmsDonationOptions(options));
  const isArabic = language === "ar";

  useEffect(() => {
    const suppliedOptions = normalizeSmsDonationOptions(options);
    if (suppliedOptions.length) {
      setConfiguredOptions(suppliedOptions);
      return undefined;
    }

    const controller = new AbortController();
    fetch(getApiUrl(endpoint), { signal: controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("SMS configuration unavailable")))
      .then((payload) => {
        const normalized = normalizeSmsDonationOptions(payload);
        if (normalized.length) setConfiguredOptions(normalized);
      })
      .catch((error) => {
        if (error.name !== "AbortError") setConfiguredOptions([]);
      });
    return () => controller.abort();
  }, [endpoint, options]);

  const allOptions = useMemo(() => mergeSmsDonationOptions(configuredOptions), [configuredOptions]);
  const visibleOptions = useMemo(
    () => allOptions.filter((option) => option.provider === activeProvider).sort((a, b) => a.amount - b.amount),
    [activeProvider, allOptions],
  );

  return (
    <section className={`sms-donation-options ${className}`.trim()} dir={isArabic ? "rtl" : undefined}>
      {showTitle ? (
        <h2><i className={iconClass} aria-hidden="true"></i> {title || (isArabic ? "طرق أخرى للتبرع" : "Other Ways to Donate")}</h2>
      ) : null}

      <div className="sms-provider-tabs" role="tablist" aria-label={isArabic ? "اختر مزود خدمة الاتصالات" : "Choose a mobile provider"}>
        {Object.entries(providerDetails).map(([provider, details]) => (
          <button
            type="button"
            role="tab"
            aria-selected={activeProvider === provider}
            className={activeProvider === provider ? "is-active" : ""}
            key={provider}
            onClick={() => setActiveProvider(provider)}
          >
            <img src={details.logo} alt={details.name} />
          </button>
        ))}
      </div>

      <div className="sms-donation-grid" role="tabpanel">
        {visibleOptions.map((option) => {
          const details = providerDetails[option.provider];
          const label = isArabic
            ? `تبرع بمبلغ ${option.amount} درهم عبر ${details.name}`
            : `Donate AED ${option.amount} by SMS with ${details.name}`;
          return (
            <a
              href={buildSmsHref(option.shortCode, option.message)}
              key={option.id ?? `${option.provider}-${option.shortCode}-${option.amount}`}
              aria-label={label}
              title={option.message ? label : `${label} — SMS message pending provider configuration`}
            >
              <span><i className={iconClass} aria-hidden="true"></i></span>
              <strong>{currency || (isArabic ? "درهم" : "AED")} {option.amount}</strong>
              <small>{codeLabel || (isArabic ? "الرمز" : "Code")} {option.shortCode}</small>
            </a>
          );
        })}
      </div>
    </section>
  );
};

export default SmsDonationOptions;
